import type * as SchemaTypes from '../types';

import type { ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment, ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment, ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment } from './ApplicationMarketTypeFragment';
import type { DealerMarketDataFragment } from './DealerMarketData';
import type { BankDealerMarketDataFragment } from './BankDealerMarketData';
import type { NzFeesDealerMarketDataFragment } from './NzFeesDealerMarketData';
import type { MyInfoSettingSpecFragment } from './MyInfoSettingSpec';
import type { DealerDisclaimersConfiguratorDataFragment } from './DealerPriceDisclaimerConfiguratorData';
import type { TranslatedStringDataFragment } from './TranslatedStringData';
import type { DealerTranslatedStringSettingDataFragment } from './DealerTranslatedStringData';
import type { AppointmentModuleSpecsFragment } from './AppointmentModuleSpecs';
import type { SimpleVersioningDataFragment } from './SimpleVersioningData';
import type { AuthorData_CorporateCustomer_Fragment, AuthorData_ExternalBank_Fragment, AuthorData_Guarantor_Fragment, AuthorData_LocalCustomer_Fragment, AuthorData_Salesforce_Fragment, AuthorData_SystemBank_Fragment, AuthorData_User_Fragment } from './AuthorData';
import type { AppointmentTimeSlotDataFragment } from './AppointmentTimeSlotData';
import type { NamirialSigningModuleSpecsFragment } from './NamirialSigningModuleSpecs';
import type { NamirialSettingsSpecFragment } from './NamirialSettingsSpec';
import type { AppointmentModuleEmailContentsSpecsFragment, AppointmentModuleEmailContentCustomerSpecsFragment, AppointmentModuleEmailContentSpecsFragment, AppointmentModuleEmailContentSalesPersonSpecsFragment, AppointmentModuleEmailContentFinderReservationSpecsFragment } from './AppointmentModuleEmailContentsSpecs';
import type { UploadFileWithPreviewFormDataFragment } from './UploadFileWithPreviewFormData';
import type { InsurerEntrypointContextDataFragment } from './InsurerEntrypointContextData';
import { gql } from '@apollo/client';
import { ApplicationMarketTypeFragmentFragmentDoc } from './ApplicationMarketTypeFragment';
import { DealerMarketDataFragmentDoc } from './DealerMarketData';
import { BankDealerMarketDataFragmentDoc } from './BankDealerMarketData';
import { NzFeesDealerMarketDataFragmentDoc } from './NzFeesDealerMarketData';
import { MyInfoSettingSpecFragmentDoc } from './MyInfoSettingSpec';
import { DealerDisclaimersConfiguratorDataFragmentDoc } from './DealerPriceDisclaimerConfiguratorData';
import { TranslatedStringDataFragmentDoc } from './TranslatedStringData';
import { DealerTranslatedStringSettingDataFragmentDoc } from './DealerTranslatedStringData';
import { AppointmentModuleSpecsFragmentDoc } from './AppointmentModuleSpecs';
import { SimpleVersioningDataFragmentDoc } from './SimpleVersioningData';
import { AuthorDataFragmentDoc } from './AuthorData';
import { AppointmentTimeSlotDataFragmentDoc } from './AppointmentTimeSlotData';
import { NamirialSigningModuleSpecsFragmentDoc } from './NamirialSigningModuleSpecs';
import { NamirialSettingsSpecFragmentDoc } from './NamirialSettingsSpec';
import { AppointmentModuleEmailContentsSpecsFragmentDoc, AppointmentModuleEmailContentCustomerSpecsFragmentDoc, AppointmentModuleEmailContentSpecsFragmentDoc, AppointmentModuleEmailContentSalesPersonSpecsFragmentDoc, AppointmentModuleEmailContentFinderReservationSpecsFragmentDoc } from './AppointmentModuleEmailContentsSpecs';
import { UploadFileWithPreviewFormDataFragmentDoc } from './UploadFileWithPreviewFormData';
import { InsurerEntrypointContextDataFragmentDoc } from './InsurerEntrypointContextData';
export type ConfiguratorApplicationEntrypointContextDataFragment = (
  { __typename: 'ConfiguratorApplicationEntrypoint' }
  & Pick<SchemaTypes.ConfiguratorApplicationEntrypoint, 'id' | 'pathname' | 'displayName'>
  & { configuratorApplicationModule: (
    { __typename: 'ConfiguratorModule' }
    & Pick<SchemaTypes.ConfiguratorModule, 'id' | 'vehicleModuleId' | 'bankModuleId' | 'insuranceModuleId' | 'customerModuleId' | 'agreementsModuleId' | 'scenarios' | 'tradeIn' | 'isTradeInAmountVisible' | 'testDrive' | 'financingPreference' | 'isInsuranceOptional' | 'showFinanceCalculator' | 'showInsuranceCalculator' | 'market' | 'paymentSettingsId' | 'myInfoSettingId' | 'isOcrEnabled' | 'depositPrice' | 'showFromValueOnVehicleDetails' | 'bankDisplayPreference' | 'externalUrl' | 'displayAppointmentDatepicker' | 'insurerDisplayPreference' | 'insurerIds'>
    & { marketType: (
      { __typename: 'DefaultApplicationMarket' }
      & ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment
      & ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment
    ) | (
      { __typename: 'NewZealandApplicationMarket' }
      & ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment
      & ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment
    ) | (
      { __typename: 'SingaporeApplicationMarket' }
      & ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment
      & ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment
    ), paymentSettings?: SchemaTypes.Maybe<(
      { __typename: 'AdyenPaymentSetting' }
      & Pick<SchemaTypes.AdyenPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'FiservPaymentSetting' }
      & Pick<SchemaTypes.FiservPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'PayGatePaymentSetting' }
      & Pick<SchemaTypes.PayGatePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'PorschePaymentSetting' }
      & Pick<SchemaTypes.PorschePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    ) | (
      { __typename: 'TtbPaymentSetting' }
      & Pick<SchemaTypes.TtbPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
    )>, myInfoSetting?: SchemaTypes.Maybe<(
      { __typename: 'MyInfoSetting' }
      & MyInfoSettingSpecFragment
    )>, company: (
      { __typename: 'Company' }
      & Pick<SchemaTypes.Company, 'id' | 'timeZone' | 'countryCode'>
    ), liveChatSetting?: SchemaTypes.Maybe<(
      { __typename: 'UserlikeChatbotSetting' }
      & Pick<SchemaTypes.UserlikeChatbotSetting, 'script' | 'id'>
    ) | (
      { __typename: 'WhatsappLiveChatSetting' }
      & Pick<SchemaTypes.WhatsappLiveChatSetting, 'link' | 'id'>
    )>, promoCodeModule?: SchemaTypes.Maybe<(
      { __typename: 'AdyenPaymentModule' }
      & Pick<SchemaTypes.AdyenPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AppointmentModule' }
      & Pick<SchemaTypes.AppointmentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiCalculationModule' }
      & Pick<SchemaTypes.AudiCalculationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiFinancingModule' }
      & Pick<SchemaTypes.AudiFinancingModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AudiSalesForceModule' }
      & Pick<SchemaTypes.AudiSalesForceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'AutoplayModule' }
      & Pick<SchemaTypes.AutoplayModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BankModule' }
      & Pick<SchemaTypes.BankModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BasicSigningModule' }
      & Pick<SchemaTypes.BasicSigningModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'BmwModule' }
      & Pick<SchemaTypes.BmwModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'CapModule' }
      & Pick<SchemaTypes.CapModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'ConfiguratorModule' }
      & Pick<SchemaTypes.ConfiguratorModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'ConsentsAndDeclarationsModule' }
      & Pick<SchemaTypes.ConsentsAndDeclarationsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'CtsModule' }
      & Pick<SchemaTypes.CtsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'EventApplicationModule' }
      & Pick<SchemaTypes.EventApplicationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderApplicationPrivateModule' }
      & Pick<SchemaTypes.FinderApplicationPrivateModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderApplicationPublicModule' }
      & Pick<SchemaTypes.FinderApplicationPublicModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FinderVehicleManagementModule' }
      & Pick<SchemaTypes.FinderVehicleManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'FiservPaymentModule' }
      & Pick<SchemaTypes.FiservPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'GiftVoucherModule' }
      & Pick<SchemaTypes.GiftVoucherModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'InsuranceModule' }
      & Pick<SchemaTypes.InsuranceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'LabelsModule' }
      & Pick<SchemaTypes.LabelsModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'LocalCustomerManagementModule' }
      & Pick<SchemaTypes.LocalCustomerManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MaintenanceModule' }
      & Pick<SchemaTypes.MaintenanceModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MobilityModule' }
      & Pick<SchemaTypes.MobilityModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'MyInfoModule' }
      & Pick<SchemaTypes.MyInfoModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'NamirialSigningModule' }
      & Pick<SchemaTypes.NamirialSigningModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PayGatePaymentModule' }
      & Pick<SchemaTypes.PayGatePaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PorscheMasterDataModule' }
      & Pick<SchemaTypes.PorscheMasterDataModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PorschePaymentModule' }
      & Pick<SchemaTypes.PorschePaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'PromoCodeModule' }
      & Pick<SchemaTypes.PromoCodeModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'SdmModule' }
      & Pick<SchemaTypes.SdmModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'SimpleVehicleManagementModule' }
      & Pick<SchemaTypes.SimpleVehicleManagementModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'StandardApplicationModule' }
      & Pick<SchemaTypes.StandardApplicationModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'TradeInModule' }
      & Pick<SchemaTypes.TradeInModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'TtbPaymentModule' }
      & Pick<SchemaTypes.TtbPaymentModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'UserlikeChatbotModule' }
      & Pick<SchemaTypes.UserlikeChatbotModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'WebsiteModule' }
      & Pick<SchemaTypes.WebsiteModule, 'id' | 'displayName'>
    ) | (
      { __typename: 'WhatsappLiveChatModule' }
      & Pick<SchemaTypes.WhatsappLiveChatModule, 'id' | 'displayName'>
    )>, priceDisclaimer: (
      { __typename: 'DealerDisclaimersConfigurator' }
      & DealerDisclaimersConfiguratorDataFragment
    ), termsTitle: (
      { __typename: 'DealerTranslatedStringSetting' }
      & DealerTranslatedStringSettingDataFragment
    ), termsText: (
      { __typename: 'DealerTranslatedStringSetting' }
      & DealerTranslatedStringSettingDataFragment
    ), appointmentModule?: SchemaTypes.Maybe<(
      { __typename: 'AppointmentModule' }
      & AppointmentModuleSpecsFragment
    )>, insurers: Array<(
      { __typename: 'Insurer' }
      & InsurerEntrypointContextDataFragment
    )> }
  ) }
);

export const ConfiguratorApplicationEntrypointContextDataFragmentDoc = /*#__PURE__*/ gql`
    fragment ConfiguratorApplicationEntrypointContextData on ConfiguratorApplicationEntrypoint {
  id
  pathname
  displayName
  configuratorApplicationModule {
    id
    vehicleModuleId
    bankModuleId
    insuranceModuleId
    customerModuleId
    agreementsModuleId
    scenarios
    tradeIn
    isTradeInAmountVisible
    testDrive
    financingPreference
    isInsuranceOptional
    showFinanceCalculator
    showInsuranceCalculator
    market
    marketType {
      ...ApplicationMarketTypeFragment
    }
    paymentSettingsId
    paymentSettings {
      id
      displayName
      paymentModuleId
    }
    myInfoSettingId
    myInfoSetting {
      ...MyInfoSettingSpec
    }
    company {
      id
      timeZone
      countryCode
    }
    isOcrEnabled
    tradeIn
    testDrive
    paymentSettingsId
    depositPrice
    marketType {
      ...ApplicationMarketTypeFragment
    }
    liveChatSetting {
      id
      ... on WhatsappLiveChatSetting {
        link
      }
      ... on UserlikeChatbotSetting {
        script
      }
    }
    promoCodeModule {
      id
      displayName
    }
    priceDisclaimer {
      ...DealerDisclaimersConfiguratorData
    }
    termsTitle {
      ...DealerTranslatedStringSettingData
    }
    termsText {
      ...DealerTranslatedStringSettingData
    }
    showFromValueOnVehicleDetails
    bankDisplayPreference
    externalUrl
    appointmentModule {
      ...AppointmentModuleSpecs
    }
    displayAppointmentDatepicker
    insurerDisplayPreference
    insurerIds
    insurers {
      ...InsurerEntrypointContextData
    }
  }
}
    `;